import * as React from 'react';
import { composer } from '../../../component-decorators/composer';
import { config } from '../../../config';
import type { IRoute } from '../../..';
import { Add, Confirm } from '@wix/wix-ui-icons-common';
import s from './selector-button.scss';
import { biLogger } from '../../../bi';
import { appMarketSubCategoryClick } from '@wix/bi-logger-market/v2';
import { Button } from '@wix/design-system';
import { AppMarketLink } from '../../app-market-link';
import { Path } from '../../../enums/marketplace-enums';
import { toJS } from 'mobx';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import type { MarketplaceStore } from '../../../component-decorators/with-marketplace-store';

export interface ISelectorButtonProps
  extends Partial<InjectedExperimentsProps> {
  marketplaceStore?: MarketplaceStore;
  name: string;
  slug: string;
  categoryName: string;
  subCategoriesCount: number;
  categorySlug?: string;
  isSelected?: boolean;
}

const subCatLinkUrl = (categorySlug, slug, route: IRoute) => {
  if (slug === 'all') {
    return `${Path.CATEGORY}/${categorySlug}`;
  }

  const selectedSubCategories = route.subCategories
    ? toJS(route.subCategories)
    : [];
  const isSelected =
    selectedSubCategories.findIndex((selected) => selected === slug) > -1;

  const newSubCategories = isSelected
    ? selectedSubCategories.filter((subSlug) => subSlug !== slug)
    : [...selectedSubCategories, slug];

  if (newSubCategories.length > 0) {
    return `${Path.CATEGORY}/${categorySlug}/${newSubCategories
      .sort()
      .join('__')}`;
  }
  return `${Path.CATEGORY}/${categorySlug}`;
};

export const SelectorButton = composer()
  .withMarketplaceStore()
  .withExperiments()
  .compose(
    ({
      name,
      slug,
      categoryName,
      marketplaceStore,
      subCategoriesCount,
      categorySlug,
      isSelected,
    }: ISelectorButtonProps) => {
      return (
        <AppMarketLink
          to={subCatLinkUrl(categorySlug, slug, marketplaceStore.route)}
          className={s.btnLink}
        >
          <Button
            className={s.selectorButton}
            size="tiny"
            skin="light"
            prefixIcon={
              isSelected ||
              wasButtonSelected(
                slug,
                marketplaceStore.route,
                subCategoriesCount,
              ) ? (
                <Confirm />
              ) : (
                <Add />
              )
            }
            priority={
              wasButtonSelected(
                slug,
                marketplaceStore.route,
                subCategoriesCount,
              )
                ? 'primary'
                : 'secondary'
            }
            onClick={createOnClickFunction(
              slug,
              name,
              categoryName,
              marketplaceStore,
              subCategoriesCount,
            )}
            dataHook={`${slug}-selector`}
          >
            {name}
          </Button>
        </AppMarketLink>
      );
    },
  );

const wasButtonSelected = (
  slug: string,
  route: IRoute,
  subCategoriesCount: number,
): boolean => {
  const subCategories = route.subCategories || [];
  return slug === 'all'
    ? subCategories.length === 0 || subCategories.length === subCategoriesCount
    : subCategories.includes(slug) &&
        subCategories.length !== subCategoriesCount;
};

const createOnClickFunction = (
  slug: string,
  name: string,
  categoryName: string,
  marketplaceStore: MarketplaceStore,
  subCategoriesCount: number,
) => {
  let subCategories = marketplaceStore.route.subCategories || [];

  if (slug === 'all') {
    subCategories = undefined;
  } else {
    const selectedAllSubCategories =
      !subCategories.includes(slug) &&
      subCategories.length + 1 === subCategoriesCount;
    if (selectedAllSubCategories) {
      subCategories = undefined;
    } else {
      subCategories = wasButtonSelected(
        slug,
        marketplaceStore.route,
        subCategoriesCount,
      )
        ? subCategories.filter((subCategory) => subCategory !== slug)
        : [...subCategories, slug];
      subCategories = subCategories.length ? subCategories : undefined;
    }
  }

  return () => {
    sendSubCategoryClickBi(
      slug,
      name,
      categoryName,
      marketplaceStore,
      subCategoriesCount,
    );
    marketplaceStore.setRoute({ ...marketplaceStore.route, subCategories });

    config.goto({ ...marketplaceStore.route, subCategories });
  };
};

const sendSubCategoryClickBi = (
  slug: string,
  name: string,
  categoryName: string,
  marketplaceStore: MarketplaceStore,
  subCategoriesCount: number,
) => {
  const clickedToIncludeSubCategory =
    slug === 'all'
      ? true
      : !wasButtonSelected(slug, marketplaceStore.route, subCategoriesCount);

  biLogger.report(
    appMarketSubCategoryClick({
      type: clickedToIncludeSubCategory ? 'include' : 'exclude',
      subcategory_name: slug.toLowerCase().replace(/-/g, '_'),
      category_name: categoryName.toLowerCase(),
      msid: marketplaceStore.metaSiteId,
    }),
  );
};
